import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  message,
  Typography,
  Space,
  Spin
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EditOutlined
} from '@ant-design/icons';

import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";

const { Title } = Typography;

const Profile = ({ isDarkMode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const { jsonApi } = useApis();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = {}; // Add any query parameters if needed
        const res = await jsonApi(appConstant.home, params, null, null);
        const { name, email, phone } = res.data || {};
        setInitialValues({ name, email, phone });
        form.setFieldsValue({ name, email, phone });
      } catch (error) {
        console.error('Error fetching user data:', error);
        message.error('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Replace this with your actual update API if needed
      const res = await jsonApi(appConstant.updateProfile, values, null, 'POST');
      message.success('Profile updated successfully');
      setInitialValues(values);
      setEditing(false);
    } catch (error) {
      console.error('Update failed:', error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(initialValues);
    setEditing(false);
  };

  const cardStyle = {
    background: isDarkMode ? '#1f1f1f' : '#ffffff',
    borderColor: isDarkMode ? '#303030' : '#f0f0f0',
  };

  const textStyle = {
    color: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.85)',
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
          <Title level={2} style={textStyle}>Profile</Title>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditing(!editing)}
            disabled={loading}
          >
            {editing ? 'Cancel Edit' : 'Edit'}
          </Button>
        </div>

        {loading ? (
          <Spin />
        ) : (
          <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={handleSubmit}
            disabled={!editing}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="name"
                  label={<span style={textStyle}>Full Name</span>}
                  rules={[{ required: true, message: 'Please enter your name' }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Full Name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="email"
                  label={<span style={textStyle}>Email</span>}
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Enter a valid email' }
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="Email" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="phone"
                  label={<span style={textStyle}>Phone</span>}
                  rules={[
                    { required: true, message: 'Please enter your phone number' },
                    { pattern: /^[+\d\s-]+$/, message: 'Enter a valid phone number' }
                  ]}
                >
                  <Input prefix={<PhoneOutlined />} placeholder="Phone" />
                </Form.Item>
              </Col>
            </Row>

            {editing && (
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save
                  </Button>
                  <Button onClick={handleReset}>Reset</Button>
                </Space>
              </Form.Item>
            )}
          </Form>
        )}
      </Card>
    </div>
  );
};

export default Profile;
